import { z } from 'zod';
/**
 * Vocabulario de la plataforma.
 *
 * Es el mismo conjunto de valores que los enums de PostgreSQL
 * (`apps/api/src/database/schema/enums.ts`) y que `apps/api/src/common/domain.enums.ts`.
 * Aca vive la version publicable: la consumen la UI de monitoreo, el servidor
 * MCP y DecoraPOS. Agregar un valor obliga a tocar tambien la migracion del
 * enum de PostgreSQL; renombrar o quitar uno es un breaking change.
 */
/** Sistemas externos con los que conversa el middleware. */
export const systemSchema = z.enum([
    'janis',
    'sap',
    'altanet',
    'multivende',
    'hubspot',
    'syncro',
    'decorapos',
    'invoicer',
    'n8n',
    /** Banco Central de Chile: el dolar observado que se carga en SAP. */
    'bcentral',
    /** POS legado (Rails): ventas, NC y logistica hacia F2 (POS-506/507). */
    'pos',
    /** Goodworking: el CRM B2B de proyectos (reservas de stock en SAP, F21). */
    'goodworking',
]);
/**
 * Alias del mismo enum con el nombre que usa el nucleo de la API
 * (`externalSystemSchema`). Existe para que ningun modulo tenga que inventarse
 * su propia copia.
 */
export const externalSystemSchema = systemSchema;
/** Valores del enum, en orden. Util para poblar selectores y catalogos. */
export const SYSTEMS = systemSchema.options;
/** Flujos del plan (docs/PLAN.md). Se nombran igual en branches, PRs y tests. */
export const flowIdSchema = z.enum([
    'F1',
    'F2',
    'F3',
    'F4',
    'F5',
    'F6',
    'F7',
    'F8',
    'F9',
    'F10',
    'F11',
    'F12',
    'F13',
    'F14',
    'F15',
    'F16',
    'F17',
    /** Janis → Middleware → DecoraPOS: disponibilidad vendible (2026-09-25). */
    'F18',
    /** DecoraPOS → Middleware: recepciones de reposición en tienda (2026-09-25). */
    'F19',
    /** Syncro → HubSpot: catálogo CRM por SKU (MIDDLE-4). */
    'F20',
    /** Goodworking → Middleware → SAP: reserva de proyecto como OV CRM_<projectId> (2026-10-27). */
    'F21',
    /**
     * Goodworking → Middleware → SAP: pedidos de entrega de las reservas de F21
     * (ST a BOD27 y rebaja de la OV) (2026-11-02). Cola propia `flow.F22` para el
     * pedido y las revisiones de su ST, con el lease de la reserva.
     */
    'F22',
    /**
     * Syncro → Middleware → Goodworking: catalogo para el CRM (2026-10-29). El
     * mismo contrato v1 que F13, con su propia tabla, cola, candado y
     * recuperacion: un CRM caido no atrasa a DecoraPOS.
     */
    'F23',
    /**
     * SAP → Middleware → Goodworking: disponibilidad por bodega y SKU para el CRM
     * (2026-10-29). La respuesta de F14, separada de DecoraPOS; desde el
     * 2026-11-02 la lee por SKU con 20 o menos (`mwF24Stock`).
     */
    'F24',
    /**
     * Middleware → SAP: la entrega de mercaderia de las ordenes del middleware,
     * creada DESPUES de que Janis facturo y desconto el fisico (decision C del
     * 2026-10-31, `docs/entrega-sap-despues-de-janis.md`). Cola propia con candado
     * por pedido, no la de F10. El numero es una propuesta (lo confirma JP).
     */
    'F25',
]);
export const FLOW_IDS = flowIdSchema.options;
/** Sentido del mensaje respecto del middleware. */
export const messageDirectionSchema = z.enum(['inbound', 'outbound']);
/**
 * Ciclo de vida de un mensaje en el message log.
 *
 * received -> queued -> processing -> processed
 *                              \-> failed -> (reintento) -> processing | dead
 * `quarantined` es un estado terminal aparte: el payload que no valida contra
 * su schema nunca se descarta, queda visible para corregirlo y reprocesarlo.
 */
export const messageStatusSchema = z.enum([
    'received',
    'queued',
    'processing',
    'processed',
    'failed',
    'quarantined',
    'dead',
]);
/** Resultado de un intento individual de procesamiento. */
export const attemptOutcomeSchema = z.enum(['ok', 'error']);
/** Motivo por el que un payload termina en cuarentena. */
export const quarantineReasonSchema = z.enum([
    'schema_validation',
    'unknown_event',
    'unmapped_entity',
]);
/** Como se cerro una dead letter desde la UI de monitoreo. */
export const deadLetterResolutionSchema = z.enum(['replayed', 'discarded', 'edited']);
/**
 * Tipos de entidad que se mapean entre sistemas en `entity_xref`.
 *
 * `brand`, `category` y `product` entraron con F12 (carga de catalogo desde
 * Syncro): Janis identifica cada una con un objectId propio, y el flujo necesita
 * resolver "esta marca de Syncro es este objectId" sin volver a preguntar.
 */
export const xrefEntityTypeSchema = z.enum([
    'order',
    'order_line',
    'invoice',
    'delivery',
    'return',
    'credit_note',
    'wms_order',
    'sku',
    'customer',
    'brand',
    'category',
    'product',
]);
/**
 * Monedas admitidas. El negocio opera en CLP; USD queda declarado para las
 * importaciones (F4). Cualquier otro codigo se rechaza en el borde a proposito:
 * una moneda desconocida en un pedido es un error de integracion, no un dato.
 */
export const currencySchema = z.enum(['CLP', 'USD']);
//# sourceMappingURL=systems.js.map